import type { ExtensionHandler } from '@atlaskit/editor-common/extensions/extension-handler';
import { getExtensionRenderer } from '@atlaskit/editor-common/utils';

import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import {
	EXTENSION_KEY,
	EXTENSION_TYPE,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';

type NonNullableExtensionHandlers = NonNullable<ADFRendererProps['extensionHandlers']>;

/**
 * Keeps Live Search on the handler that produced its server markup once the manifest is ready. The
 * manifest renders it through a different component tree, which would remount it and lose what the
 * user typed while the page loaded. Other macro-core extensions go to `fallbackExtensionHandlers`.
 *
 * TODO(CONFCLOUD-85176): Live Search's macro experience is only reported on the manifest path, so it
 * stops being reported on SSR pages while this is active.
 */
export function createLiveSearchHandoffExtensionHandlers(
	serverExtensionHandlers: NonNullableExtensionHandlers,
	fallbackExtensionHandlers: ADFRendererProps['extensionHandlers'],
): ADFRendererProps['extensionHandlers'] {
	const extensionType = EXTENSION_TYPE.MACRO_CORE;
	const serverHandler = serverExtensionHandlers[extensionType];
	const serverDelegate = serverHandler ? getExtensionRenderer(serverHandler) : undefined;

	if (!serverDelegate) {
		return fallbackExtensionHandlers;
	}

	const fallbackHandler = fallbackExtensionHandlers?.[extensionType];
	const fallbackDelegate = fallbackHandler ? getExtensionRenderer(fallbackHandler) : undefined;

	return {
		...fallbackExtensionHandlers,
		[extensionType]: (...args: Parameters<ExtensionHandler>) => {
			const [extension] = args;

			if (extension.extensionKey === EXTENSION_KEY.LIVE_SEARCH) {
				return serverDelegate(...args);
			}

			return fallbackDelegate ? fallbackDelegate(...args) : null;
		},
	};
}
